/**
 * Photographing a model, for the squares in the folded-away sidebar.
 *
 * ## Why this borrows the renderer rather than making one
 *
 * A second `WebGLRenderer` would be the obvious way, and is the wrong one. A browser allows
 * a small number of WebGL contexts at once and drops the oldest without asking when the
 * count is exceeded — which on a Quest, where contexts are scarcer and the one that matters
 * is the one drawing the room, is a way to lose the whole view to a sidebar decoration. It
 * would also upload every geometry and texture a second time, since GPU resources belong to
 * the context that created them.
 *
 * So this borrows the application's own renderer and points it at a render target instead of
 * at the canvas, for one synchronous burst. The scene it draws is its own — a light rig and
 * nothing else — and the model is moved into it and back before anything else runs. Nothing
 * on screen can catch it mid-shot: the frame loop is driven by `requestAnimationFrame`, and
 * this returns before the next one.
 *
 * ## The picture is square and the model is not
 *
 * Framing is `computeCameraDistance` at an aspect of 1, which fits the model's bounding
 * sphere inside the narrower of the two fields of view — so a long thin model is shown small
 * and whole rather than large and cropped. The viewpoint is the shared three-quarter one, so
 * that the picture in the sidebar and the model you get when you press it are seen from the
 * same place.
 *
 * The background is left transparent, so one picture serves both themes and there is no
 * cache to invalidate when the theme changes.
 */

import {
  Color,
  DirectionalLight,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderTarget,
  type Object3D,
  type WebGLRenderer,
} from 'three';

import {
  boundingRadius,
  boundsSize,
  computeCameraDistance,
  orbitOffset,
} from '../domain/scaling.js';
import type { SceneModel } from './sceneManager.js';

/**
 * How large a picture is, in pixels.
 *
 * Twice the square it is shown in, so it stays sharp on a display that packs two device
 * pixels into one CSS pixel. Past that the file grows fourfold for a detail nobody can see
 * at the size these are drawn.
 */
const THUMBNAIL_PX = 96;

/** A little wider than the flat view's framing, so nothing grazes the edge of the square. */
const THUMBNAIL_MARGIN = 1.25;

const THUMBNAIL_FOV = 35;

/**
 * A light rig of its own, rather than the room's.
 *
 * The room's key light casts a shadow onto a floor, and a picture with a transparent
 * background has no floor to catch one. Two lights and no shadows is all a thumbnail needs:
 * enough to tell a curved surface from a flat one at ninety-six pixels.
 */
const SKY_FILL = 0xdfe6ef;
const GROUND_FILL = 0x3a424c;

export class ThumbnailMaker {
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(THUMBNAIL_FOV, 1, 0.1, 10_000);
  private readonly target: WebGLRenderTarget;
  private readonly canvas = document.createElement('canvas');
  private readonly pixels: Uint8Array;
  private readonly restoreColour = new Color();
  private readonly aim = new Vector3();

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly size: number = THUMBNAIL_PX,
  ) {
    const fill = new HemisphereLight(SKY_FILL, GROUND_FILL, 1.4);
    const key = new DirectionalLight(0xffffff, 2.2);
    key.position.set(2, 4, 3);
    this.scene.add(fill, key);

    this.target = new WebGLRenderTarget(this.size, this.size, {
      // Without this the pixels come back in the renderer's working linear space and every
      // picture is noticeably darker than the model it is of.
      colorSpace: SRGBColorSpace,
      depthBuffer: true,
      // Multisampled, because the whole subject is a silhouette against nothing: an
      // unsmoothed edge at this size is the difference between a shape and a staircase.
      samples: 4,
    });

    this.canvas.width = this.size;
    this.canvas.height = this.size;
    this.pixels = new Uint8Array(this.size * this.size * 4);
  }

  /**
   * Take one picture, or nothing if it cannot be taken right now.
   *
   * Refused during an immersive session: the renderer belongs to the headset then, and
   * taking its render target away mid-frame would show up in somebody's face rather than in
   * a sidebar they are not looking at. There is nothing to do about it but wait, and a
   * missing picture is a square that fills itself in later.
   */
  async capture(model: SceneModel): Promise<Blob | undefined> {
    if (this.renderer.xr.isPresenting) return undefined;

    const { object } = model;
    const home = object.parent;
    if (!home) return undefined;

    this.frame(model);

    // Moved rather than copied: `add` keeps the object's own transform, which is the
    // centering offset, so it arrives in this scene standing on y = 0 and centred on its
    // own footprint — exactly what the camera above was aimed at.
    this.scene.add(object);

    try {
      this.draw();
    } finally {
      // Before anything else can run. A model left in this scene is a model missing from
      // the one on screen, and the frame loop is one `requestAnimationFrame` away.
      restore(home, object);
    }

    return this.toBlob();
  }

  dispose(): void {
    this.target.dispose();
  }

  /** Point the camera at the middle of the model, far enough back to hold all of it. */
  private frame(model: SceneModel): void {
    const [, heightMm] = boundsSize(model.bounds);
    const radius = boundingRadius(model.bounds);

    // A model with no extent at all — an empty file that still parsed — would put the
    // camera on top of itself and divide by zero on the way. One millimetre gives it a
    // frame to be empty in.
    const distance = computeCameraDistance(
      Math.max(radius, 1),
      THUMBNAIL_FOV,
      1,
      THUMBNAIL_MARGIN,
    );

    this.aim.set(0, heightMm / 2, 0);
    this.camera.position.set(...orbitOffset(distance)).add(this.aim);
    this.camera.lookAt(this.aim);
    this.camera.near = Math.max(distance / 1000, 0.001);
    this.camera.far = distance * 100;
    this.camera.updateProjectionMatrix();
  }

  /** One render into the target, leaving the renderer as it was found. */
  private draw(): void {
    this.renderer.getClearColor(this.restoreColour);
    const clearAlpha = this.renderer.getClearAlpha();

    this.renderer.setRenderTarget(this.target);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    this.renderer.readRenderTargetPixels(
      this.target,
      0,
      0,
      this.size,
      this.size,
      this.pixels,
    );

    this.renderer.setRenderTarget(null);
    this.renderer.setClearColor(this.restoreColour, clearAlpha);
  }

  /** Turn the pixels just read into something an `<img>` can be pointed at. */
  private async toBlob(): Promise<Blob | undefined> {
    const context = this.canvas.getContext('2d');
    if (!context) return undefined;

    context.putImageData(this.readable(), 0, 0);

    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => {
        resolve(blob ?? undefined);
      }, 'image/png');
    });
  }

  /**
   * The pixels as a 2D canvas wants them, which is not how WebGL hands them over.
   *
   * Two differences, both of which show if either is missed. WebGL numbers its rows from
   * the bottom, so the picture arrives upside down. And it blends with premultiplied alpha
   * while `ImageData` is not premultiplied, so every partly transparent pixel — which is
   * every pixel along the model's outline — would come out too dark, leaving a dirty fringe
   * round an otherwise clean shape.
   */
  private readable(): ImageData {
    const image = new ImageData(this.size, this.size);

    for (let row = 0; row < this.size; row += 1) {
      const from = (this.size - 1 - row) * this.size * 4;
      const to = row * this.size * 4;

      for (let column = 0; column < this.size * 4; column += 4) {
        const alpha = this.pixels[from + column + 3] ?? 0;
        const scale = alpha === 0 ? 0 : 255 / alpha;

        image.data[to + column] = (this.pixels[from + column] ?? 0) * scale;
        image.data[to + column + 1] = (this.pixels[from + column + 1] ?? 0) * scale;
        image.data[to + column + 2] = (this.pixels[from + column + 2] ?? 0) * scale;
        image.data[to + column + 3] = alpha;
      }
    }

    return image;
  }
}

function restore(home: Object3D, object: Object3D): void {
  home.add(object);
}
